import { cloneElement, useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent, ReactElement, Ref } from "react";
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
} from "@floating-ui/react";
import type { Placement } from "@floating-ui/react";

const HOVER_OPEN_DELAY_MS = 400;
const HOVER_CLOSE_DELAY_MS = 100;
const LONG_PRESS_MS = 500;
const LONG_PRESS_MOVE_TOLERANCE_PX = 8;
const fullscreenRoot = (): HTMLElement | undefined =>
  typeof document !== "undefined" && document.fullscreenElement instanceof HTMLElement
    ? document.fullscreenElement
    : undefined;

const NATIVELY_FOCUSABLE_TAGS = ["a", "button", "input", "select", "textarea"];

type FloatingTooltipProps = {
  content: string;
  children: ReactElement;
  placement?: Placement;
  shouldOpen?: () => boolean;
  disabled?: boolean;
  testId?: string;
  ariaHidden?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  wrap?: boolean;
  wrapperClassName?: string;
};

type ChildProps = {
  ref?: Ref<Element>;
  disabled?: boolean;
  onPointerDown?: (event: ReactPointerEvent<Element>) => void;
  onPointerUp?: (event: ReactPointerEvent<Element>) => void;
  onPointerCancel?: (event: ReactPointerEvent<Element>) => void;
  onPointerMove?: (event: ReactPointerEvent<Element>) => void;
};

export const FloatingTooltip = ({
  content,
  children,
  placement = "top",
  shouldOpen,
  disabled = false,
  testId,
  ariaHidden = false,
  open: controlledOpen,
  onOpenChange,
  wrap = false,
  wrapperClassName = "",
}: FloatingTooltipProps) => {
  const inactive = disabled || content === "";
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = !inactive && (isControlled ? controlledOpen : uncontrolledOpen);
  const pressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pressOriginRef = useRef<{ x: number; y: number } | null>(null);

  const canOpen = () => !inactive && (shouldOpen?.() ?? true);

  const setOpen = (next: boolean) => {
    if (isControlled) {
      onOpenChange?.(next);
    } else {
      setUncontrolledOpen(next);
    }
  };

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: (next) => setOpen(next ? canOpen() : false),
    placement,
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    enabled: !inactive && !isControlled,
    mouseOnly: true,
    delay: { open: HOVER_OPEN_DELAY_MS, close: HOVER_CLOSE_DELAY_MS },
    handleClose: safePolygon(),
  });
  const focus = useFocus(context, { enabled: !inactive && !isControlled });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip", enabled: !ariaHidden });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const wrappedProps = children.props as ChildProps;
  const childIsFocusable =
    typeof children.type === "string" &&
    NATIVELY_FOCUSABLE_TAGS.includes(children.type) &&
    !wrappedProps.disabled;
  const reference = wrap ? (
    <span
      className={`inline-flex [&>:disabled]:pointer-events-none [&:has(>:disabled)]:cursor-not-allowed ${wrapperClassName}`}
      tabIndex={childIsFocusable || inactive ? undefined : 0}
    >
      {children}
    </span>
  ) : (
    children
  );
  const childProps = reference.props as ChildProps;
  const childRef = wrap ? null : (children as unknown as { ref?: Ref<Element> }).ref ?? null;
  const setReference = useCallback((node: Element | null) => refs.setReference(node), [refs]);
  const setFloating = useCallback((node: HTMLDivElement | null) => refs.setFloating(node), [refs]);
  const referenceRef = useMergeRefs([setReference, childRef]);

  const clearPress = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    pressOriginRef.current = null;
  };

  useEffect(() => clearPress, []);

  const onPointerDown = (event: ReactPointerEvent<Element>) => {
    childProps.onPointerDown?.(event);
    if (event.pointerType !== "touch" || inactive) {
      return;
    }
    clearPress();
    pressOriginRef.current = { x: event.clientX, y: event.clientY };
    pressTimerRef.current = setTimeout(() => {
      pressTimerRef.current = null;
      if (canOpen()) {
        setOpen(true);
      }
    }, LONG_PRESS_MS);
  };

  const onPointerUp = (event: ReactPointerEvent<Element>) => {
    childProps.onPointerUp?.(event);
    clearPress();
  };

  const onPointerCancel = (event: ReactPointerEvent<Element>) => {
    childProps.onPointerCancel?.(event);
    clearPress();
  };

  const onPointerMove = (event: ReactPointerEvent<Element>) => {
    childProps.onPointerMove?.(event);
    const origin = pressOriginRef.current;
    if (
      origin &&
      Math.hypot(event.clientX - origin.x, event.clientY - origin.y) > LONG_PRESS_MOVE_TOLERANCE_PX
    ) {
      clearPress();
    }
  };

  return (
    <>
      {cloneElement(
        reference,
        getReferenceProps({
          ...childProps,
          ref: referenceRef,
          onPointerDown,
          onPointerUp,
          onPointerCancel,
          onPointerMove,
        }),
      )}
      {open && (
        <FloatingPortal root={fullscreenRoot()}>
          <div
            ref={setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            aria-hidden={ariaHidden || undefined}
            data-testid={testId}
            className="z-[70] max-w-xs px-2 py-1.5 text-xs leading-snug text-white bg-gray-600 shadow-lg rounded-md break-words whitespace-pre-line select-none"
          >
            {content}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
